import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { RuntimeConfigService } from './runtime-config.service';
import { PAGE_LOAD_FAILURE_TOAST_DEDUPE_WINDOW_MS } from '../../utils/page-load-failure-toast-gate.util';
import { buildPageAccessDeniedSummary } from '../../utils/page-access-denied.util';

/**
 * Carries the "you may not open this page" toast from a route guard to the
 * always-mounted shell (`MainLayoutComponent`), which is the only place a
 * toast can be rendered.
 *
 * ## Why a service, and why a held slot as well as a stream
 *
 * A guard runs before any component exists and cannot inject the
 * component-scoped `MessageService`. On a typed URL or a refresh
 * (a cold load) the shell is not even mounted yet when the guard decides, so
 * a bare `Subject` would emit into nothing and the message would be lost.
 * `stage()` therefore BOTH holds the line (read once by `consume()` when the
 * shell mounts) AND emits on `staged$` (for a guard that fires while the shell
 * is already up, e.g. a click from the sidebar). The shell drains the slot on
 * every emission, so each line is shown exactly once.
 *
 * Same shape as `AuthFailureNoticeService`, which does this for the login page.
 */
@Injectable({ providedIn: 'root' })
export class AccessDeniedNoticeService {
  // REFACTORED-DIAGNOSTIC-CODES-FLAG: remove with the flag once production is cleared.
  private readonly runtimeConfig = inject(RuntimeConfigService);
  private readonly router = inject(Router);
  // Supplies the role-aware landing page (Dashboard, or User Management for a SuperAdmin).
  private readonly auth = inject(AuthService);
  private lastDeniedAt = Number.NEGATIVE_INFINITY;
  private pendingSummary: string | null = null;
  private readonly stagedSubject = new Subject<void>();

  /** Fires when a line has been staged; the listener should call {@link consume}. */
  readonly staged$: Observable<void> = this.stagedSubject.asObservable();

  /**
   * Stage the "no permission to access this page" toast for the shell.
   *
   * REFACTORED-DIAGNOSTIC-CODES-FLAG: a no-op while `refactoredDiagnosticCodesEnabled`
   * is `false` (production). Page-level failures showed no toast before the
   * coded diagnostics, so production keeps that; the REDIRECT still happens —
   * the flag changes only what the user reads.
   */
  stageNoPermissionToAccessPage(): void {
    if (!this.runtimeConfig.refactoredDiagnosticCodesEnabled()) {
      return;
    }
    this.pendingSummary = buildPageAccessDeniedSummary();
    this.stagedSubject.next();
  }

  /**
   * A page-load call came back 403: stage the toast and leave for the user's
   * landing page (the Dashboard, or User Management for a SuperAdmin).
   *
   * For pages other than a filing (a refused filing returns to the previous
   * page instead — see `FilingFormContainerComponent`). The toast goes through
   * the shell rather than the page's own `<p-toast>` because the page is
   * destroyed by this very navigation.
   *
   * A page that fires several calls on load (User Management: listing + own
   * profile) can have them all refused at once; calls inside the toast dedupe
   * window collapse into one toast and one navigation.
   */
  denyPageAccess(): void {
    const now = Date.now();
    if (now - this.lastDeniedAt < PAGE_LOAD_FAILURE_TOAST_DEDUPE_WINDOW_MS) {
      return;
    }
    this.lastDeniedAt = now;
    this.stageNoPermissionToAccessPage();
    void this.router.navigateByUrl(this.auth.homeUrl());
  }

  /** Read and clear in one step, so the toast is shown exactly once. */
  consume(): string | null {
    const summary = this.pendingSummary;
    this.pendingSummary = null;
    return summary;
  }
}
